import { useMutation } from '@tanstack/react-query';
import {
  updateAccountAttributes,
  UpdateAccountAttributesErrorResponse,
  UpdateAccountAttributesPayload,
  UpdateAccountAttributesSuccessResponse,
} from 'TeamModule/services/UpdateAccountCsmAttributeApi';
import { AxiosResponse } from 'axios';
import { useSelector } from 'react-redux';
import { selectAuthToken } from 'selectors';
import { useNotification } from 'hooks/useNotification';
import ApiClientManager from 'core/apiClient';

export interface UpdateAccountAttributesMutationOptions {
  /** Tenant name for API context - if not provided, falls back to global tenant */
  tenant?: string;
  /** Realm ID for API context - if not provided, falls back to global realm */
  realmId?: string;
}

export const useUpdateAccountAttributesMutation = (
  options?: UpdateAccountAttributesMutationOptions,
) => {
  const token = useSelector(selectAuthToken);
  const { showSuccess, showError } = useNotification();
  const { tenant, realmId } = options || {};

  const mutation = useMutation<
    AxiosResponse<UpdateAccountAttributesSuccessResponse>,
    AxiosResponse<UpdateAccountAttributesErrorResponse>,
    UpdateAccountAttributesPayload
  >({
    mutationFn: async (params) => {
      if (!token) {
        throw new Error('No auth token available');
      }
      // Build custom headers if tenant/realmId are provided to ensure correct API context
      const config =
        tenant && realmId
          ? { headers: ApiClientManager.buildUrlHeaders({ tenantName: tenant, realmId }) }
          : {};
      return updateAccountAttributes({ params, token, config }) as Promise<
        AxiosResponse<UpdateAccountAttributesSuccessResponse>
      >;
    },
    onSuccess: (data) => {
      // ✅ FIX: Replace alert() with toast notification
      showSuccess('Update account attributes successful!');
    },
    onError: (error: any) => {
      const apiMessage = error?.data?.message || error?.response?.data?.message || error?.message;
      const apiMessageSuffix = apiMessage ? `: ${apiMessage}` : '!';
      showError(`Update account attributes failed${apiMessageSuffix}`);
    },
  });
  return {
    updateAccountAttributes: mutation.mutate,
    isUpdatingAccountAttributes: mutation.isPending,
  };
};
